import ErrorDisplay from './ErrorDisplay';
import { DataGrid, type DataGridProps, type GridColDef, type GridRowId } from '@mui/x-data-grid';
import { useMemo } from 'react';

type Id = { id: GridRowId };

interface TableProps<T> {
  rows?: T[] | undefined;
  error?: Error | undefined;
  actions?: GridColDef[] | undefined;
  initialSort?: { field: string; sort: 'asc' | 'desc' } | undefined;
  onRetry?: (() => void) | undefined;
  filterValue?: string | undefined;
}

const filterRows = <T extends Id>(
  rows: T[],
  columns: readonly GridColDef[],
  filterValue: string
): T[] => {
  if (!filterValue.trim()) return rows;

  const searchTerm = filterValue.toLowerCase().trim();

  return rows.filter(row => {
    // Search through all column fields
    for (const column of columns) {
      const field = column.field;
      const value = row[field as keyof T];

      if (value !== null && value !== undefined) {
        // Handle nested objects (like phone, invoiceAccount)
        if (typeof value === 'object') {
          const objectString = JSON.stringify(value).toLowerCase();
          if (objectString.includes(searchTerm)) return true;
        } else {
          const stringValue = String(value).toLowerCase();
          if (stringValue.includes(searchTerm)) return true;
        }
      }
    }

    // Also search through all direct string/number properties
    for (const key of Object.keys(row)) {
      const value = row[key as keyof T];
      if (typeof value === 'string' && value.toLowerCase().includes(searchTerm)) {
        return true;
      }
      if (typeof value === 'number' && String(value).includes(searchTerm)) {
        return true;
      }
    }

    return false;
  });
};

const Table = <T extends Id>({
  rows,
  columns,
  error,
  actions = [],
  initialSort,
  onRetry,
  filterValue = '',
  ...props
}: TableProps<T> & Omit<DataGridProps<T>, 'rows'>) => {
  const columnsCustomized = columns.map(column => {
    const newColumn = { ...column };

    if (column.width === undefined) {
      newColumn.flex = 1;
    }

    return newColumn;
  });

  const columnsWithActions = [...columnsCustomized, ...actions];

  const filteredRows = useMemo(
    () => filterRows(rows || [], columns, filterValue),
    [rows, columns, filterValue]
  );

  if (error) return <ErrorDisplay error={error} title="Failed to load data" onRetry={onRetry} />;

  return (
    <DataGrid
      {...props}
      rows={filteredRows}
      columns={columnsWithActions}
      pageSizeOptions={props.pageSizeOptions ?? [10, 25, 50]}
      initialState={{
        ...props.initialState,
        pagination: { paginationModel: { pageSize: 25 } },
        ...(initialSort && {
          sorting: {
            sortModel: [{ field: initialSort.field, sort: initialSort.sort }]
          }
        })
      }}
      getRowId={row => row.id}
      sx={{
        height: '100%',
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: 'none',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
        '& .MuiDataGrid-columnHeader': {
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          '&:focus, &:focus-within': {
            outline: 'none'
          }
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 700,
          fontSize: '0.875rem',
          letterSpacing: '0.02em'
        },
        '& .MuiDataGrid-sortIcon': {
          color: 'primary.contrastText'
        },
        '& .MuiDataGrid-menuIconButton': {
          color: 'primary.contrastText'
        },
        '& .MuiDataGrid-columnSeparator': {
          color: 'primary.light'
        },
        '& .MuiDataGrid-row': {
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: 'action.hover'
          },
          '&.Mui-selected': {
            backgroundColor: 'primary.light',
            '&:hover': {
              backgroundColor: 'primary.light'
            }
          }
        },
        '& .MuiDataGrid-row:nth-of-type(even)': {
          backgroundColor: 'grey.50',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        },
        '& .MuiDataGrid-cell': {
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          '&:focus, &:focus-within': {
            outline: 'none'
          }
        },
        '& .MuiDataGrid-footerContainer': {
          backgroundColor: 'grey.100',
          borderTop: '2px solid',
          borderColor: 'divider'
        },
        '& .MuiTablePagination-root': {
          color: 'text.secondary'
        }
      }}
    />
  );
};

export default Table;
